'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Is It Plastic? Every Material in Your Kitchen, Answered"
      author="MicroPlastics Research Desk"
      date="August 15, 2026"
      readTime="13 min read"
      heroImage="/blog/heroes/general.webp"
      heroImageAlt="Is it plastic? Thirty common household materials — polyester, nylon, silicone, melamine, acrylic, PLA, bamboo composite and more — sorted into plastic and not plastic"
    >
      <BlogLastUpdated iso="2026-08-15" formatted="August 15, 2026" />

      <BlogTLDR>
        <strong>Nineteen of the thirty materials in an ordinary kitchen are
        plastic. Most people can name about six.</strong>{' '}
        Polyester, nylon and acrylic are plastic — they are fabrics made from
        the same polymer families as bottles and packaging.{' '}
        <strong>Melamine is plastic</strong>, a thermoset resin, which is why
        unbreakable picnic plates are not ceramic.{' '}
        <strong>PLA is plastic</strong> — &ldquo;plant-based&rdquo; and
        &ldquo;compostable&rdquo; describe where it came from and where it can
        go, not what it does in your cup. <strong>Silicone is the genuinely
        interesting one:</strong> it is a synthetic polymer, but built on a
        silicon&ndash;oxygen backbone rather than a carbon chain, so it is not a
        plastic in the usual sense — more heat-stable than any of them, and far
        less studied. And <strong>&ldquo;bamboo fibre&rdquo; tableware is
        usually bamboo powder bound in melamine resin</strong>: a plastic dish
        with bamboo in it. The whole list is below, each one linked to the full
        profile.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>If it is a synthetic fabric, it is plastic.</strong> Polyester, nylon and acrylic are textiles spun from polymers — the same chemistry as packaging, in fibre form.</>,
          <><strong>Melamine is plastic.</strong> A thermoset resin with a wood-flour or paper filler. Unbreakable picnic and kids&rsquo; plates are moulded plastic, not ceramic.</>,
          <><strong>&ldquo;Compostable&rdquo; does not mean &ldquo;not plastic&rdquo;.</strong> PLA is a polymer and fragments like one; the claim is about origin and disposal, not behaviour in use.</>,
          <><strong>Silicone sits in its own category.</strong> A siloxane, not a carbon-chain polymer. More thermally stable than any common plastic — and much less researched, which cuts both ways.</>,
          <><strong>&ldquo;Bamboo fibre&rdquo; is usually melamine.</strong> Bamboo powder bound in resin. The EU has withdrawn these from sale because bamboo is not an authorised filler for plastic food-contact materials.</>,
          <><strong>The resin code only appears on some things.</strong> Numbers 1&ndash;7 cover rigid packaging; fabrics, coatings and tableware usually carry nothing at all.</>,
          <><strong>&ldquo;Is it plastic?&rdquo; is the wrong final question.</strong> What matters is whether it sheds — and that is driven by heat, abrasion and age, not by the polymer name.</>,
        ]}
      />

      <KeyStats
        heading="The short version"
        stats={[
          { value: '19 of 30', label: 'common household materials that are plastic', context: 'counting fabrics, coatings and tableware, not just packaging', source: 'MicroPlastics material profiles, August 2026', sourceUrl: 'https://microplasticsapp.com/materials' },
          { value: '7', label: 'resin codes, covering only rigid packaging', context: 'polyester clothing, non-stick coatings and melamine plates generally carry no code at all', source: 'ASTM D7611 resin identification coding system', sourceUrl: 'https://www.astm.org/d7611_d7611m-20.html' },
          { value: '3', label: 'things that actually drive shedding', context: 'heat, abrasion and age — the polymer name matters far less than how you treat it', source: 'Li et al., Nature Food, 2020 — polypropylene release under heat', sourceUrl: 'https://www.nature.com/articles/s43016-020-00171-y' },
          { value: '2035', label: 'when EU nail products must stop using added microplastics', context: 'the longest transition of any category, against 2027 for rinse-off cosmetics', source: 'Regulation (EU) 2023/2055', sourceUrl: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
        ]}
      />

      <h2>The fast answer</h2>
      <p>
        Every material below links to its full profile — what it sheds, under
        what conditions, and what to use instead.
      </p>

      <BlogComparisonTable
        caption="Common household materials, sorted (checked August 2026)"
        columns={[
          { key: 'm', label: 'Material' },
          { key: 'a', label: 'Plastic?' },
          { key: 'w', label: 'What it actually is' },
        ]}
        rows={[
          { m: 'Polyester', a: 'Yes', w: 'PET in fibre form — the same polymer as a drinks bottle' },
          { m: 'Nylon', a: 'Yes', w: 'Polyamide; utensils, bristles, activewear' },
          { m: 'Acrylic', a: 'Yes', w: 'PMMA — Perspex, and the powder in acrylic nails' },
          { m: 'Polypropylene (#5)', a: 'Yes', w: 'Tubs, caps, straws; the most common cap plastic' },
          { m: 'Polyethylene / HDPE / LDPE', a: 'Yes', w: 'Bags, bottles, film, squeeze tubes' },
          { m: 'PET (#1)', a: 'Yes', w: 'Single-use bottles and most food trays' },
          { m: 'PVC (#3)', a: 'Yes', w: 'Cling film, tubing, some flooring' },
          { m: 'Polystyrene (#6)', a: 'Yes', w: 'Foam cups, takeaway boxes, disposable cutlery' },
          { m: 'Polycarbonate (#7)', a: 'Yes', w: 'Polymerised from BPA itself; the legacy hard-bottle plastic' },
          { m: 'Tritan copolyester', a: 'Yes', w: 'BPA-free by structure; the current default drinkware plastic' },
          { m: 'ABS', a: 'Yes', w: 'Appliance housings, toys, press-on nails' },
          { m: 'Melamine', a: 'Yes', w: 'Thermoset resin — unbreakable picnic and kids’ plates' },
          { m: 'EVA', a: 'Yes', w: 'Foam mats, shoe soles, flexible tubing' },
          { m: 'Polyurethane', a: 'Yes', w: 'Foam, coatings, stretch fibres' },
          { m: 'PLA “bioplastic”', a: 'Yes', w: 'Plant-derived, still a polymer, still fragments' },
          { m: 'PTFE (Teflon)', a: 'Yes', w: 'Fluoropolymer non-stick coating' },
          { m: 'Silicone', a: 'Sort of', w: 'Siloxane backbone, not a carbon chain — synthetic polymer, not a plastic in the usual sense' },
          { m: 'Bamboo composite', a: 'Depends', w: '“Bamboo fibre” tableware is usually bamboo powder in melamine resin' },
          { m: 'Natural rubber / latex', a: 'No', w: 'Tapped from a tree; an elastomer, not a plastic' },
          { m: 'Stainless steel', a: 'No', w: 'Inert alloy — but check the lid' },
          { m: 'Aluminium', a: 'No', w: 'Metal, though cans are usually lined with a polymer' },
          { m: 'Cast iron', a: 'No', w: 'Metal; seasoning is polymerised oil, not plastic' },
          { m: 'Glass', a: 'No', w: 'Inert; the zero-shed reference point' },
          { m: 'Ceramic and stoneware', a: 'No', w: 'Fired mineral; check glazes rather than polymers' },
          { m: 'Hardwood', a: 'No', w: 'Solid timber — unlike most “composite” boards' },
          { m: 'Paper and paperboard', a: 'No', w: 'Cellulose — but cups and cartons are frequently plastic-lined' },
          { m: 'Cotton and linen', a: 'No', w: 'Natural fibres; blends are the thing to read for' },
          { m: 'Beeswax wrap', a: 'No', w: 'Cotton and wax; the genuine cling-film alternative' },
        ]}
      />

      <p>
        Full profiles:{' '}
        <Link href="/materials/polyester" className="text-forest underline">polyester</Link>,{' '}
        <Link href="/materials/nylon" className="text-forest underline">nylon</Link>,{' '}
        <Link href="/materials/acrylic" className="text-forest underline">acrylic</Link>,{' '}
        <Link href="/materials/polypropylene" className="text-forest underline">polypropylene</Link>,{' '}
        <Link href="/materials/polyethylene" className="text-forest underline">polyethylene</Link>,{' '}
        <Link href="/materials/hdpe" className="text-forest underline">HDPE</Link>,{' '}
        <Link href="/materials/ldpe" className="text-forest underline">LDPE</Link>,{' '}
        <Link href="/materials/pet" className="text-forest underline">PET</Link>,{' '}
        <Link href="/materials/pvc" className="text-forest underline">PVC</Link>,{' '}
        <Link href="/materials/polystyrene" className="text-forest underline">polystyrene</Link>,{' '}
        <Link href="/materials/polycarbonate" className="text-forest underline">polycarbonate</Link>,{' '}
        <Link href="/materials/tritan" className="text-forest underline">Tritan</Link>,{' '}
        <Link href="/materials/abs" className="text-forest underline">ABS</Link>,{' '}
        <Link href="/materials/melamine" className="text-forest underline">melamine</Link>,{' '}
        <Link href="/materials/eva" className="text-forest underline">EVA</Link>,{' '}
        <Link href="/materials/polyurethane" className="text-forest underline">polyurethane</Link>,{' '}
        <Link href="/materials/pla-bioplastic" className="text-forest underline">PLA</Link>,{' '}
        <Link href="/materials/ptfe-teflon" className="text-forest underline">PTFE</Link>,{' '}
        <Link href="/materials/silicone" className="text-forest underline">silicone</Link>,{' '}
        <Link href="/materials/bamboo-composite" className="text-forest underline">bamboo composite</Link>,{' '}
        <Link href="/materials/natural-rubber-latex" className="text-forest underline">natural rubber</Link>,{' '}
        <Link href="/materials/stainless-steel" className="text-forest underline">stainless steel</Link>,{' '}
        <Link href="/materials/aluminium" className="text-forest underline">aluminium</Link>,{' '}
        <Link href="/materials/cast-iron" className="text-forest underline">cast iron</Link>,{' '}
        <Link href="/materials/glass-borosilicate" className="text-forest underline">glass</Link>,{' '}
        <Link href="/materials/ceramic-stoneware" className="text-forest underline">ceramic</Link>,{' '}
        <Link href="/materials/wood-hardwood" className="text-forest underline">hardwood</Link>,{' '}
        <Link href="/materials/cellulose-paperboard" className="text-forest underline">paperboard</Link>,{' '}
        <Link href="/materials/natural-cotton-linen" className="text-forest underline">cotton and linen</Link>,{' '}
        <Link href="/materials/natural-beeswax-cotton" className="text-forest underline">beeswax wrap</Link>.
      </p>

      <InlineGuessReveal
        source="is_it_plastic_silicone"
        question="Is silicone a plastic?"
        options={[
          { label: 'Yes — it is just a soft plastic' },
          { label: 'No — it is a natural material' },
          { label: 'Neither, exactly — it is a siloxane', correct: true },
          { label: 'Only the coloured kinds are' },
        ]}
        answer="Neither — silicone has a silicon–oxygen backbone"
        answerNote={
          <>
            Every common plastic is built on a chain of carbon atoms. Silicone
            is not: its backbone alternates silicon and oxygen, which makes it a
            synthetic polymer but not a plastic in the ordinary sense. That
            structure is why it tolerates heat no carbon-chain plastic
            survives. The honest caveat is that it is also studied far less
            than PET or polypropylene, so &ldquo;safer&rdquo; partly reflects
            fewer people having looked.
          </>
        }
        ctaText="The lid, the seal and the coating are usually three different materials."
        ctaLabel="Scan a product"
      />

      <RiskQuizInlineNudge source="blog_inline_is_it_plastic" />

      <h2>The four that catch people out</h2>
      <p>
        <strong>Melamine.</strong> If a plate is light, unbreakable and slightly
        warm to the touch, it is not ceramic — it is melamine-formaldehyde
        resin. It is genuinely plastic, and it is the standard material for
        picnic sets and children&rsquo;s tableware. It also has a specific
        weakness: heat and acid both increase migration from it, which is why it
        should never go in a microwave.
      </p>
      <p>
        <strong>PLA.</strong> Marketed as plant-based and compostable, and both
        are true — of its origin and its disposal route. Neither is a claim
        about what it does while holding your iced coffee. It is a polymer, it
        fragments like one, and &ldquo;compostable&rdquo; in practice usually
        means an industrial facility most councils do not operate.
      </p>
      <p>
        <strong>Bamboo composite.</strong> The one where the marketing is
        actively misleading. A bamboo cutting board is timber strips glued with
        resin. But &ldquo;bamboo fibre&rdquo; cups and plates are typically
        bamboo powder bound in melamine — a plastic dish with a plant filler.
        The EU has withdrawn these from sale in several member states, because
        bamboo is not an authorised filler for plastic food-contact materials
        and the composites released more melamine and formaldehyde under heat
        than plain melamine did.
      </p>
      <p>
        <strong>Polyester.</strong> The one that surprises people most, because
        it does not look like plastic at all. It is PET — the same polymer as a
        water bottle — extruded into fibre. Roughly speaking, wearing a
        polyester fleece is wearing several hundred bottles&rsquo; worth of the
        same material, and it sheds every time it is washed. See{' '}
        <Link href="/blog/microplastics-in-laundry" className="text-forest underline">microplastics in laundry</Link>{' '}
        for what to do about it.
      </p>

      <BlogAppCTA
        headline="Point your camera at it instead of guessing"
        sub="Most products are three or four materials at once — a Tritan body, a polypropylene cap, a silicone seal, a printed coating. Scan any product with MicroPlastics to identify what it is made of and get a 0–100 score."
      />

      <h2>How to tell when nothing is labelled</h2>
      <p>
        The recycling triangle with a number in it — the resin identification
        code — only appears on rigid packaging, and even then inconsistently. It
        covers seven categories:
      </p>
      <ul>
        <li><strong>1 &mdash; PET.</strong> Clear, glossy, slightly flexible. Drinks bottles, food trays.</li>
        <li><strong>2 &mdash; HDPE.</strong> Opaque, waxy. Milk jugs, detergent bottles.</li>
        <li><strong>3 &mdash; PVC.</strong> Cling film, tubing. The one to avoid near heat.</li>
        <li><strong>4 &mdash; LDPE.</strong> Soft, floppy film. Bread bags, squeeze bottles.</li>
        <li><strong>5 &mdash; PP.</strong> Rigid, heat-tolerant, slightly springy. Tubs, caps, straws.</li>
        <li><strong>6 &mdash; PS.</strong> Brittle or foamed. Takeaway boxes, disposable cutlery.</li>
        <li><strong>7 &mdash; Other.</strong> A catch-all: polycarbonate, Tritan, ABS, PLA and more all land here, so the number tells you almost nothing on its own.</li>
      </ul>
      <p>
        Everything outside rigid packaging &mdash; clothing, non-stick coatings,
        melamine plates, foam, sealants &mdash; usually carries no code at all.
        For those, the care label, the feel and the sound are better evidence
        than the packaging: melamine rings dull rather than sharp, polyester
        feels slick and does not crease like cotton, and a genuinely ceramic
        plate is heavy for its size.
      </p>

      <h2>Why the question matters less than you think</h2>
      <p>
        Having sorted thirty materials into plastic and not plastic, here is the
        honest conclusion:{' '}
        <strong>knowing the polymer name tells you surprisingly little about
        your exposure.</strong>
      </p>
      <p>
        A polypropylene tub used cold and hand-washed sheds very little. The
        same tub microwaved weekly for three years sheds a great deal. The
        drivers are always the same three things &mdash; <strong>heat, abrasion
        and age</strong> &mdash; and they dominate the differences between
        polymers. That is why a scratched, elderly &ldquo;good&rdquo; plastic
        is worse than a new &ldquo;bad&rdquo; one, and why the most useful
        habit is not memorising resin codes but noticing which of your things
        are old, scratched, and repeatedly heated.
      </p>

      <h2>What to actually do</h2>
      <ul>
        <li><strong>Retire anything scratched.</strong> Surface damage is fragmentation already in progress, whatever the material.</li>
        <li><strong>Move heat off plastic first.</strong> Microwaving and dishwashing do more than swapping polymers ever will. Never microwave melamine.</li>
        <li><strong>Check the lid, seal and coating separately.</strong> They are almost always different materials from the body, and usually softer ones.</li>
        <li><strong>Read &ldquo;bamboo fibre&rdquo; as &ldquo;melamine&rdquo;</strong> until the packaging proves otherwise.</li>
        <li><strong>Treat &ldquo;compostable&rdquo; as a disposal claim</strong>, not a materials one.</li>
        <li><strong>Wash synthetics less, cooler, and fuller.</strong> The fabric in your wardrobe is plastic, and laundry is its main release route.</li>
      </ul>

      <p>
        Related:{' '}
        <Link href="/blog/worst-microplastic-ingredients" className="text-forest underline">the worst microplastic ingredients</Link>,{' '}
        <Link href="/blog/refillable-water-bottle-materials-compared" className="text-forest underline">bottle materials compared</Link>,{' '}
        <Link href="/blog/is-tritan-a-microplastic" className="text-forest underline">what Tritan actually is</Link>, and{' '}
        <Link href="/blog/microwaving-plastic-containers-microplastics" className="text-forest underline">microwaving plastic containers</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Is polyester plastic?',
            answer: 'Yes. Polyester is polyethylene terephthalate — PET, the same polymer used for single-use drinks bottles — extruded into fibre instead of moulded into a container. That is why it does not absorb water, does not crease like cotton, and sheds microplastic fibres in the wash. Roughly speaking, a polyester fleece is the fibre form of the same material as the bottle in your fridge.',
          },
          {
            question: 'Is nylon plastic?',
            answer: 'Yes. Nylon is a polyamide, a synthetic polymer used for cooking utensils, brush bristles, hosiery and activewear. In cookware it matters because nylon utensils soften and abrade against hot pans, and in textiles because nylon sheds fibres during washing much as polyester does.',
          },
          {
            question: 'Is silicone plastic?',
            answer: 'Not in the usual sense, and this is the one genuine grey area. Every common plastic is built on a carbon backbone; silicone is a siloxane, with a backbone alternating silicon and oxygen atoms. It is a synthetic polymer, but a structurally different class, and it tolerates temperatures no carbon-chain plastic survives. The honest caveat is that silicone has been studied far less than PET or polypropylene, so its better reputation partly reflects less scrutiny rather than proven superiority.',
          },
          {
            question: 'Is melamine plastic?',
            answer: 'Yes. Melamine tableware is melamine-formaldehyde, a thermoset resin usually containing a wood-flour or paper filler. It is the standard material for unbreakable picnic sets and children’s plates, which is why they feel light and warm rather than heavy and cold like ceramic. It should never be microwaved: heat and acidic foods both increase migration of melamine and formaldehyde from the surface.',
          },
          {
            question: 'Is bamboo tableware plastic?',
            answer: 'Usually yes, despite the name. A solid bamboo cutting board is timber strips bonded with resin. But "bamboo fibre" cups, plates and children’s tableware are typically bamboo powder bound in melamine resin — a plastic item with a plant filler. Several EU member states have withdrawn these products because bamboo is not an authorised filler for plastic food-contact materials, and testing found the composites released more melamine and formaldehyde under heat than conventional melamine did.',
          },
          {
            question: 'Is PLA or “compostable” plastic still plastic?',
            answer: 'Yes. PLA is a polymer made from plant sugars rather than petroleum, so "plant-based" is accurate about its origin and "compostable" is accurate about one disposal route — typically an industrial composting facility, which most household waste streams do not reach. Neither claim describes how it behaves while in use, and it fragments into microplastics much as conventional polymers do.',
          },
          {
            question: 'Does the recycling number tell me what a plastic is?',
            answer: 'Only partly, and only on rigid packaging. Codes 1 to 6 map to specific polymers — PET, HDPE, PVC, LDPE, polypropylene and polystyrene — but code 7 is a catch-all containing polycarbonate, Tritan, ABS, PLA and others with very different properties, so it tells you almost nothing. Clothing, non-stick coatings, melamine tableware and foam usually carry no resin code at all.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'MicroPlastics Research Desk', year: 2026, title: 'Material profiles: composition, shedding behaviour under heat, abrasion, fat and acid, and evidence grade for 30 common household materials', publisher: 'MicroPlastics', url: 'https://microplasticsapp.com/materials' },
          { authors: 'European Commission', year: 2023, title: 'Regulation (EU) 2023/2055 amending REACH Annex XVII entry 78 — synthetic polymer microparticles and staggered transition periods by product category', publisher: 'EUR-Lex', url: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
          { authors: 'European Commission / RASFF', year: 2024, title: 'Bamboo-melamine food contact materials: bamboo is not an authorised additive under Regulation (EU) No 10/2011; market withdrawals across multiple member states', publisher: 'Food Packaging Forum', url: 'https://foodpackagingforum.org/news/bamboo-in-plastic-fcms-not-authorized-in-the-eu' },
          { authors: 'Li Y, et al.', year: 2020, title: 'Microplastic release from the degradation of polypropylene feeding bottles during infant formula preparation', publisher: 'Nature Food', url: 'https://www.nature.com/articles/s43016-020-00171-y' },
        ]}
      />
    </BlogPostLayout>
  )
}
